"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { User, Mail, Phone, MessageSquare, Send, Building2, Pencil, ChevronDown } from "lucide-react";
import { BlockProps } from "../BlocksManager/registry";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";

export default function FormulaireContactAgence({ data }: BlockProps) {
  if (!data) return null;

  const pathname = usePathname();
  const agencySlug = pathname.split("/").filter(Boolean).pop() || "";
  const agencyName = agencySlug.charAt(0).toUpperCase() + agencySlug.slice(1);

  const variantKey = String(data.page || "").trim().toUpperCase();
  const allOptions: Record<string, string[]> = {
    CHEFS_ENTREPRISE_RH_CSE: ["Chef d'entreprise", "RH", "CSE"],
    SALARIES: ["Salarié"],
    RESEAU_ETABLISSEMENTS: ["Réseau d'établissements"],
    CONCEPT: ["Chef d'entreprise", "RH", "CSE", "Salarié", "Réseau d'établissements"],
  };
  const profilOptions = allOptions[variantKey] || allOptions["CONCEPT"];
  
  const profilMap: Record<string, string> = {
    "chef d'entreprise": "Chef d'entreprise",
    "chef d'entreprises": "Chef d'entreprise",
    rh: "RH",
    cse: "CSE",
    salarié: "Salarié",
    salarie: "Salarié",
    "réseau d'établissements": "Réseau d'établissements",
    "reseau d'etablissements": "Réseau d'établissements",
  };

  // Déterminer si on utilise "de" ou "d'"
  const voyelles = ['a', 'e', 'i', 'o', 'u', 'h'];
  const premiereLettre = agencyName.charAt(0).toLowerCase();
  const preposition = voyelles.includes(premiereLettre) ? "d'" : "de ";

  const [formData, setFormData] = useState({
    profil: "",
    entreprise: "",
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    adresse: "",
    sujet: "",
    message: "",
    accepte: false,
  });

  const [addressDetails, setAddressDetails] = useState({
    street: "",
    postcode: "",
    city: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.accepte) {
      alert("Veuillez accepter la politique de confidentialité");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_API_URL;

      const normalizedProfil = (formData.profil || "").toLowerCase().trim();
      const strapiProfil = profilMap[normalizedProfil] || formData.profil;

      const strapiData = {
        data: {
          profil: strapiProfil,
          entreprise: formData.entreprise || "",
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone || "",
          indicatif: "FR",
          adresse_livraison: formData.adresse || "",
          street: addressDetails.street || formData.adresse || "",
          ville: addressDetails.city || "",
          code_postale: addressDetails.postcode || "",
          sujet: `[Régalice PauseDéj'] - ${formData.sujet || `contact ${agencySlug}`}`,
          agence_slug: agencySlug,
          message: formData.message,
          accepte_politique: true,
          utm_source: urlParams.get("utm_source") || "",
          utm_campaign: urlParams.get("utm_campaign") || "",
          utm_content: urlParams.get("utm_content") || "",
          utm_term: urlParams.get("utm_term") || "",
          utm_medium: urlParams.get("utm_medium") || "",
          gclid: urlParams.get("gclid") || "",
        },
      };

      const response = await fetch(`${STRAPI_URL}/contact-agences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(strapiData),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          profil: "",
          entreprise: "",
          nom: "",
          prenom: "",
          email: "",
          telephone: "",
          adresse: "",
          sujet: "",
          message: "",
          accepte: false,
        });
        setAddressDetails({
          street: "",
          postcode: "",
          city: "",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("❌ Erreur Strapi:", errorData);
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative overflow-hidden py-16 lg:py-24" style={{ backgroundColor: '#CCD4FB' }} key={data.id}>
      {/* Cercles décoratifs */}
      <div className="absolute -left-20 -top-20 h-[300px] w-[300px] rounded-full bg-violet opacity-30" />
      <div className="absolute -right-20 -top-10 h-[200px] w-[200px] rounded-full bg-violet opacity-20" />
      <div className="absolute -right-32 bottom-0 h-[400px] w-[400px] rounded-full bg-violet opacity-20" />

      <div className="container relative z-10 mx-auto px-6 lg:px-16">
        <div className="mx-auto max-w-3xl">
          {/* Titre */}
          <h2 className="text-center font-literata text-[40px] lg:text-[56px] font-bold leading-[1.1] text-text-body mb-12">
            {data.titre || `Contacter l'agence ${preposition}${agencyName} !`}
          </h2>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profil et Entreprise */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="relative">
                <Building2 className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
                <select
                  value={formData.profil}
                  name="profil"
                  onChange={(e) => setFormData((prev) => ({ ...prev, profil: e.target.value }))}
                  className="w-full appearance-none rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-10 text-[16px] text-text-body outline-none focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                  required
                >
                  {!pathname.startsWith("/repas-entreprise") && <option value="" disabled>Je suis un(e)*</option>}
                  {profilOptions.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              </div>
              <div className="relative">
                <Building2 className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
                <input
                  type="text"
                  name="entreprise"
                  value={formData.entreprise}
                  onChange={handleChange}
                  placeholder="Nom de l'entreprise"
                  className="w-full rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-6 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                />
              </div>
            </div>

            {/* Nom et Prénom */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="relative">
                <User className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Nom"
                  required
                  className="w-full rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-6 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                />
              </div>
              <div className="relative">
                <User className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  placeholder="Prénom"
                  required
                  className="w-full rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-6 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                />
              </div>
            </div>

            {/* Email et Téléphone */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  required
                  className="w-full rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-6 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  placeholder="Téléphone"
                  required
                  className="w-full rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-6 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                />
              </div>
            </div>

            {/* Adresse de livraison */}
            <div>
              <AddressAutocomplete
                name="adresse"
                value={formData.adresse}
                onChange={(value, details) => {
                  setFormData((prev) => ({ ...prev, adresse: value }));
                  if (details) {
                    setAddressDetails(details);
                  }
                }}
                placeholder="Adresse de livraison"
                required
                className="w-full rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-12 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
                iconClassName="text-violet"
              />
            </div>

            {/* Sujet de la demande */}
            <div className="relative">
              <Pencil className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
              <select
                value={formData.sujet}
                name="sujet"
                onChange={(e) => setFormData((prev) => ({ ...prev, sujet: e.target.value }))}
                required
                className="w-full appearance-none rounded-full border-2 border-transparent bg-white py-4 pl-12 pr-10 text-[16px] text-text-body outline-none focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)]"
              >
                <option value="" disabled>Sujet de la demande</option>
                <option value="Demande d'informations sur nos offres">Demande d'informations sur nos offres</option>
                <option value="Demande de devis">Demande de devis</option>
                <option value="Support client">Support client</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-violet" />
            </div>

            {/* Message */}
            <div className="relative">
              <MessageSquare className="absolute left-4 top-6 h-5 w-5 text-violet" />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Message"
                rows={5}
                required
                className="w-full rounded-[28px] border-2 border-transparent bg-white py-4 pl-12 pr-6 text-[16px] text-text-body outline-none placeholder:text-text-body/50 focus:border-violet focus:shadow-[0_8px_18px_rgba(124,106,242,0.22)] resize-none"
              />
            </div>

            {/* Checkbox RGPD */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="accepte"
                id="accepte"
                checked={formData.accepte}
                onChange={handleChange}
                required
                className="mt-1 h-5 w-5 rounded border-2 border-green text-green focus:ring-2 focus:ring-green cursor-pointer"
              />
              <label htmlFor="accepte" className="text-[14px] leading-[1.5] text-text-body cursor-pointer">
                J'accepte que mes données soient utilisées pour traiter ma demande conformément à la{" "}
                <a href="/politique-confidentialite" className="underline text-violet hover:text-violet/80">
                  Politique de confidentialité
                </a>
                .
              </label>
            </div>

            {/* Bouton Envoyer */}
            <div className="flex justify-center pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !formData.accepte}
                className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-[18px] font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#8181FE' }}
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer"}
                <Send className="h-5 w-5" />
              </button>
            </div>

            {/* Messages de statut */}
            {submitStatus === "success" && (
              <div className="text-center text-green font-semibold">
                Votre message a été envoyé avec succès !
              </div>
            )}
            {submitStatus === "error" && (
              <div className="text-center text-pink font-semibold">
                Une erreur est survenue. Veuillez réessayer.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
